
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit, Trash2, Users2, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Player } from '@/types/player';
import { Video } from '@/types/video';
import { transformPlayerData } from '@/utils/playerTransform';
import { ImageUpload } from '@/components/ui/image-upload';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { VideoManager } from '@/components/VideoManager';

interface PlayerWithProfile extends Player {
  team_name?: string;
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface Team {
  id: string;
  name: string;
  club_id: string;
}

const VOLLEYBALL_POSITIONS = [
  'Libero',
  'Middle Blocker',
  'Opposite / Right Side',
  'Outside Hitter',
  'Setter',
  'Defensive Specialist'
];

export const PlayerManagement = () => {
  const [players, setPlayers] = useState<PlayerWithProfile[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<PlayerWithProfile | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [hometown, setHometown] = useState('');
  const [height, setHeight] = useState('');
  const [gradYear, setGradYear] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [dominantHand, setDominantHand] = useState('');
  const [videos, setVideos] = useState<Video[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('players')
        .select(`
          *,
          profile:profiles(first_name, last_name, email),
          team:teams(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedPlayers: PlayerWithProfile[] = (data || []).map(player => {
        const transformed = transformPlayerData(player);
        return {
          ...transformed,
          team_name: player.team?.name
        };
      });

      setPlayers(transformedPlayers);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, role')
        .eq('role', 'member')
        .order('first_name');

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error('Failed to load profiles');
    }
  };

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name, club_id')
        .order('name');

      if (error) throw error;
      setTeams(data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    }
  };

  useEffect(() => {
    fetchPlayers();
    fetchProfiles();
    fetchTeams();
  }, []);

  const handlePositionChange = (position: string, checked: boolean) => {
    if (checked) {
      setSelectedPositions(prev => [...prev, position]);
    } else {
      setSelectedPositions(prev => prev.filter(p => p !== position));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProfileId) return;

    setIsSubmitting(true);
    try {
      const playerData = {
        profile_id: selectedProfileId,
        team_id: selectedTeamId === 'no-team' ? null : selectedTeamId || null,
        position: selectedPositions.length > 0 ? selectedPositions.join(', ') : null,
        jersey_number: jerseyNumber ? parseInt(jerseyNumber) : null,
        hometown: hometown || null,
        height: height || null,
        grad_year: gradYear ? parseInt(gradYear) : null,
        profile_picture_url: profilePictureUrl || null,
        dominant_hand: dominantHand || null,
        // Convert Video objects to plain objects for database storage
        video_urls: videos.map(video => ({
          id: video.id,
          url: video.url,
          source: video.source,
          title: video.title,
          description: video.description,
          created_at: video.created_at
        })),
      };

      if (editingPlayer) {
        const { error } = await supabase
          .from('players')
          .update(playerData)
          .eq('id', editingPlayer.id);

        if (error) throw error;
        toast.success('Player updated successfully');
      } else {
        const { error } = await supabase
          .from('players')
          .insert([playerData]);

        if (error) throw error;
        toast.success('Player created successfully');
      }

      handleDialogClose();
      fetchPlayers();
    } catch (error) {
      console.error('Error saving player:', error);
      toast.error('Failed to save player');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (player: PlayerWithProfile) => {
    setEditingPlayer(player);
    setSelectedProfileId(player.profile_id);
    setSelectedTeamId(player.team_id || 'no-team');
    // Parse existing positions from comma-separated string
    const positions = player.position ? player.position.split(', ').map(p => p.trim()) : [];
    setSelectedPositions(positions);
    setJerseyNumber(player.jersey_number?.toString() || '');
    setHometown(player.hometown || '');
    setHeight(player.height || '');
    setGradYear(player.grad_year?.toString() || '');
    setProfilePictureUrl(player.profile_picture_url || '');
    setDominantHand(player.dominant_hand || '');
    setVideos(Array.isArray(player.video_urls) ? 
      player.video_urls.filter((v): v is Video => typeof v === 'object' && v !== null && 'id' in v) : 
      []);
    setIsDialogOpen(true);
  };

  const handleDelete = async (playerId: string) => {
    if (!confirm('Are you sure you want to delete this player?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('players')
        .delete()
        .eq('id', playerId);

      if (error) throw error;
      toast.success('Player deleted successfully');
      fetchPlayers();
    } catch (error) {
      console.error('Error deleting player:', error);
      toast.error('Failed to delete player');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingPlayer(null);
    setSelectedProfileId('');
    setSelectedTeamId('');
    setSelectedPositions([]);
    setJerseyNumber('');
    setHometown('');
    setHeight('');
    setGradYear('');
    setProfilePictureUrl('');
    setDominantHand('');
    setVideos([]);
  };

  const getAvailableProfiles = () => {
    if (editingPlayer) {
      return profiles;
    }
    // For new players, only show profiles that aren't already players
    const existingPlayerProfileIds = players.map(p => p.profile_id);
    return profiles.filter(p => !existingPlayerProfileIds.includes(p.id));
  };

  if (loading) {
    return <div>Loading players...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Players</h3>
          <p className="text-sm text-muted-foreground">
            Manage player profiles and assignments
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Player
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPlayer ? 'Edit Player' : 'Create New Player'}
              </DialogTitle>
              <DialogDescription>
                {editingPlayer ? 'Update the player details.' : 'Select a user profile and assign them as a player.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                <ImageUpload
                  currentImageUrl={profilePictureUrl}
                  onImageUploaded={setProfilePictureUrl}
                  onImageRemoved={() => setProfilePictureUrl('')}
                  label="Player Profile Picture"
                />
                
                <div className="space-y-2">
                  <Label htmlFor="profile-select">User Profile</Label>
                  <Select 
                    value={selectedProfileId} 
                    onValueChange={setSelectedProfileId} 
                    required
                    disabled={!!editingPlayer}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a user profile" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableProfiles().map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.first_name} {profile.last_name} ({profile.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="team-select">Team (Optional)</Label>
                    <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-team">No Team</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Positions (Select multiple)</Label>
                    <div className="grid grid-cols-2 gap-2 p-3 border rounded-md max-h-32 overflow-y-auto">
                      {VOLLEYBALL_POSITIONS.map((position) => (
                        <div key={position} className="flex items-center space-x-2">
                          <Checkbox
                            id={position}
                            checked={selectedPositions.includes(position)}
                            onCheckedChange={(checked) => handlePositionChange(position, checked as boolean)}
                          />
                          <Label htmlFor={position} className="text-sm font-normal">
                            {position}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="jersey-number">Jersey Number</Label>
                    <Input
                      id="jersey-number"
                      type="number"
                      value={jerseyNumber}
                      onChange={(e) => setJerseyNumber(e.target.value)}
                      placeholder="Jersey #"
                      min="1"
                      max="99"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="height">Height</Label>
                    <Input
                      id="height"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      placeholder="e.g., 6'2&quot;"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="grad-year">Grad Year</Label>
                    <Input
                      id="grad-year"
                      type="number"
                      value={gradYear}
                      onChange={(e) => setGradYear(e.target.value)}
                      placeholder="2024"
                      min="2020"
                      max="2035"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hometown">Hometown</Label>
                    <Input
                      id="hometown"
                      value={hometown}
                      onChange={(e) => setHometown(e.target.value)}
                      placeholder="e.g., San Diego, CA"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Dominant Hand</Label>
                    <RadioGroup value={dominantHand} onValueChange={setDominantHand}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Right" id="right" />
                        <Label htmlFor="right">Right</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Left" id="left" />
                        <Label htmlFor="left">Left</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                <VideoManager 
                  videos={videos}
                  onVideosChange={setVideos}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingPlayer ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Player</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Team</TableHead>
            <TableHead>Position</TableHead>
            <TableHead>Jersey #</TableHead>
            <TableHead>Height</TableHead>
            <TableHead>Hometown</TableHead>
            <TableHead>Grad Year</TableHead>
            <TableHead>Dominant Hand</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {players.map((player) => (
            <TableRow key={player.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={player.profile_picture_url} />
                    <AvatarFallback>
                      {player.profile.first_name[0]}{player.profile.last_name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span>{player.profile.first_name} {player.profile.last_name}</span>
                </div>
              </TableCell>
              <TableCell>{player.profile.email}</TableCell>
              <TableCell>{player.team_name || '-'}</TableCell>
              <TableCell>{player.position || '-'}</TableCell>
              <TableCell>{player.jersey_number || '-'}</TableCell>
              <TableCell>{player.height || '-'}</TableCell>
              <TableCell>{player.hometown || '-'}</TableCell>
              <TableCell>{player.grad_year || '-'}</TableCell>
              <TableCell>{player.dominant_hand || '-'}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/player/${player.id}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(player)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(player.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {players.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                No players found. Create your first player to get started.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
